import React from "react";
import { Box, Typography, Link, Stack, Paper, Divider, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import dayjs from "dayjs";
import { useTitle } from "../../hooks/useTitle";
import { useUILabel } from "../../hooks/useUILabel";
import { UploadFile, CloudDownload, Description } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useFilePicker } from "use-file-picker";
import { useAddProjectMutation } from "../../services/project.service";
import { toast } from "react-toastify";
import Papa from "papaparse";
import { useAddLocationMutation } from "../../services/location.service";
import { getAddressComponentFromGeocoder } from "../../utils/helper";
import { useGetCountriesQuery } from "../../services/country.service";
import { Project } from "../../types/project";
import { GMAP_API_KEY } from "../../configs/api";
import { CardBase } from "../../components/global";

const myTimezone = dayjs.tz.guess();

const StyledUploadButton = styled(LoadingButton)(({ theme }) => ({
  borderRadius: 12,
  padding: "14px 32px",
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  backgroundColor: "#12903C",
  color: "#ffffff",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: "#0f7a32",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(18, 144, 60, 0.4)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 20px",
    fontSize: "0.875rem",
    width: "100%",
  },
}));

const StyledResultBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.mode === "dark"
    ? "rgba(255, 255, 255, 0.05)"
    : "rgba(0, 0, 0, 0.02)",
  borderRadius: 16,
  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
  maxHeight: "500px",
  overflowY: "auto",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}60, transparent)`,
    borderRadius: "16px 16px 0 0",
  },
  "&::-webkit-scrollbar": {
    width: "10px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(0, 0, 0, 0.05)",
    borderRadius: "5px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.3)"
      : "rgba(0, 0, 0, 0.3)",
    borderRadius: "5px",
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.4)"
        : "rgba(0, 0, 0, 0.4)",
    },
  },
  "& pre": {
    margin: 0,
    fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
    fontSize: "0.875rem",
    lineHeight: 1.8,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: theme.palette.text.primary,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    borderRadius: 12,
    maxHeight: "400px",
    "& pre": {
      fontSize: "0.75rem",
      lineHeight: 1.6,
    },
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  color: "#12903C",
  fontWeight: 600,
  textDecoration: "none",
  padding: theme.spacing(0.75, 1.5),
  borderRadius: 8,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    color: "#0f7a32",
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(18, 144, 60, 0.15)"
      : "rgba(18, 144, 60, 0.08)",
    transform: "translateX(4px)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 2px 8px rgba(0, 0, 0, 0.2)"
      : "0 2px 8px rgba(18, 144, 60, 0.15)",
  },
}));

const StyledTemplateBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: 16,
  background: theme.palette.mode === "dark"
    ? "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)"
    : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.08)"}`,
  boxShadow: theme.palette.mode === "dark"
    ? "0 4px 20px rgba(0, 0, 0, 0.3)"
    : "0 4px 20px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.4)"
      : "0 8px 32px rgba(0, 0, 0, 0.12)",
    borderColor: theme.palette.primary.main + "40",
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderRadius: 12,
  },
}));

interface CreateProjectData {
  project_title: string;
  description: string;
  confidential_information: string;
  manager: string;
  start_date: string;
  timezone: string;
  is_private: string;
  type: string;
  hourly_rate: string;
  total_pay: string;
  approximate_hours_to_complete: string;
  mile_radius: string;
  skills: string;
  num_of_talent: string;

  location_id: string;
  address: string;
  primary_contact_name: string;
  primary_contact_phone_number: string;
  primary_contact_email: string;
  location_name: string;
  site_number: string;

  is_photo_required: string;
  tool_required: string;
  //covid_vaccine_required: string;
  background_check_required: string;
  drug_test_required: string;
}

const ProjectBulkUploadPage = () => {
  const bulkUploadWorkLabel = useUILabel("project.bulkUploadWork");
  const uploadMultipleWorksLabel = useUILabel("project.uploadMultipleWorks");
  
  useTitle(bulkUploadWorkLabel);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = React.useState(false);
  const [addProject, { isLoading: addingProject }] = useAddProjectMutation();
  const [addLocation, { isLoading: addingLocation }] = useAddLocationMutation();
  const { data: countries } = useGetCountriesQuery();
  const [resultStrings, setResultStrings] = React.useState("");

  const [openFileSelector, { plainFiles, errors, clear }] = useFilePicker({
    multiple: false,
    accept: [".csv"],
    maxFileSize: 50,
    readFilesContent: false,
  });
  const [file, setFile] = React.useState<File | null>();

  React.useEffect(() => {
    errors?.[0]?.fileSizeTooSmall && toast.error("File size is too small!");
    errors?.[0]?.fileSizeToolarge && toast.error("File size is too large!");
    errors?.[0]?.readerError &&
      toast.error("Problem occured while reading file!");
    errors?.[0]?.maxLimitExceeded && toast.error("Too many files");
    errors?.[0]?.minLimitNotReached && toast.error("Not enought files");
  }, [errors]);

  const addLocationService = async (payload: any) => {
    const loc = await addLocation(payload).unwrap();
    return loc;
  };
  const addProjectService = async (payload: any) => {
    const project = await addProject(payload).unwrap();
    return project;
  };

  const doParse = (file: File) => {
    if (!file) return;
    // @ts-ignore
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      //The callback to execute when parsing is complete.
      complete: async function (results: {
        data: CreateProjectData[];
        errors: [];
        meta: {};
      }) {
        doUpload(results.data);
      },
    });
  };

  const doUpload = async (data: CreateProjectData[]) => {
    setLoading(true);
    setResultStrings("Loading..please wait");

    const results = await Promise.all(
      data.map(async (row, index) => {
        try {
          const {
            location_name,
            address,
            primary_contact_email,
            primary_contact_name,
            primary_contact_phone_number,
            site_number,
          } = row;

          const skillsArray = row.skills.split(",").map((skill) => {
            return Number(skill);
          });

          const {
            project_title,
            description,
            start_date,
            confidential_information,
            // num_of_talent,
            is_private,
            type,
            hourly_rate,
            approximate_hours_to_complete,
            mile_radius,
            manager,
            is_photo_required,
            tool_required,
            //covid_vaccine_required,
            background_check_required,
            drug_test_required,
            total_pay,
            timezone,
          } = row;

          let location_id = Number(row.location_id);

          // if location_id not defined, create new location based on google map
          if (!row.location_id) {
            const resp = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
                address
              )}&key=${GMAP_API_KEY}`
            );
            const geoResult = await resp.json();
            //console.log("geoResult", geoResult);

            if (geoResult.status == "OK") {
              const res1 = geoResult.results[0];
              const { lat, lng } = res1.geometry.location;
              //console.log("latlng", res1.geometry.location);

              const addressComp = getAddressComponentFromGeocoder(
                res1.address_components
              );
              //console.log("addressComp", addressComp);
              const {
                city,
                country,
                postal_code,
                state,
                street,
                street_number,
              } = addressComp;

              const address_1 = `${street_number} ${street} `;
              const found = countries?.find(
                (it) => it.code.toLowerCase() == country.toLowerCase()
              );

              const addLocationRequest = {
                name: location_name,
                site_number,
                address_1,
                city,
                state,
                zip: postal_code,
                country: found?.id,
                lat,
                lng,
                primary_contact_name,
                primary_contact_phone_number,
                primary_contact_email,
              };
              const location = await addLocationService(addLocationRequest);
              location_id = location.id;
            } else {
              return `[row-${index + 1
                }] Get location FAILED. response from google: ${geoResult.status
                }`;
            }
          }

          const createProjectPayload = {
            title: project_title,
            description,
            start_date: dayjs(start_date).format("YYYY-MM-DD HH:mm"),
            timezone: timezone,
            manager: Number(manager),
            type,
            is_private: Number(is_private),
            num_of_talent: 1, //Number(num_of_talent),
            confidential_information,

            location: location_id,

            hourly_rate,
            total_pay: Number(total_pay),
            approximate_hours_to_complete: Number(
              approximate_hours_to_complete
            ),
            mile_radius: Number(mile_radius),
            skills: skillsArray,

            is_photo_required: is_photo_required?.toUpperCase() === "TRUE",
            tool_required: tool_required?.toUpperCase() === "TRUE",
            //covid_vaccine_required:
            //covid_vaccine_required?.toUpperCase() === "TRUE",
            background_check_required:
              background_check_required?.toUpperCase() === "TRUE",
            drug_test_required: drug_test_required?.toUpperCase() === "TRUE",
          };

          const project: Project = await addProjectService(
            createProjectPayload
          );
          return `[row-${index + 1}] Project #${project.id} - ${project.title
            } successfully created`;
        } catch (err) {
          return `[row-${index + 1}] Project creation failed. Error:${err}`;
        }
      })
    );
    setFile(null);
    clear(); //clear file
    toast.info("Upload completed.");
    setResultStrings(results.join("\n"));
    setLoading(false);
  };

  React.useEffect(() => {
    if (plainFiles?.length > 0) {
      const file = plainFiles[0];
      doParse(file);
    }
  }, [plainFiles]);

  return (
    <Box sx={{ px: { xs: 0, sm: 3 }, py: { xs: 2, sm: 3 }, minHeight: "100vh" }}>
      <CardBase
        title={bulkUploadWorkLabel}
        subtitle={uploadMultipleWorksLabel}
        useGlassCard={true}
        nested={false}
      >
        <Stack spacing={{ xs: 2.5, sm: 4 }}>
          {/* Template Download Section */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: { xs: 2, sm: 3 },
                color: "text.secondary",
                fontSize: { xs: "0.8rem", sm: "0.9rem" },
                lineHeight: 1.6,
              }}
            >
              Before uploading, make sure your CSV file follows the correct format. Download the template below to see the required structure and column names.
            </Typography>
            <StyledTemplateBox>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  borderRadius: 2,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.05)",
                  border: (theme) =>
                    `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.1)"}`,
                }}
              >
                <Description
                  sx={{
                    color: "#12903C",
                    fontSize: { xs: "1.5rem", sm: "2rem" },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, width: { xs: "100%", sm: "auto" } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: { xs: 0.25, sm: 0.5 },
                    fontSize: { xs: "1rem", sm: "1.125rem" },
                    color: "text.primary",
                  }}
                >
                  CSV Template
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mb: { xs: 1, sm: 1.5 },
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  }}
                >
                  Download the sample template to ensure your CSV file has the correct format and all required columns.
                </Typography>
                <StyledLink
                  target="_blank"
                  href="https://firebasestorage.googleapis.com/v0/b/the-method-329619.appspot.com/o/template-bulk-upload-csv.csv?alt=media&token=35c95ccb-3b7a-4c2a-b59c-4ad595b50357"
                  sx={{
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    padding: { xs: "0.5rem 1rem", sm: "0.75rem 1.5rem" },
                  }}
                >
                  <CloudDownload sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }} />
                  Download Template
                </StyledLink>
              </Box>
            </StyledTemplateBox>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Upload Section */}
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
                mb: { xs: 2, sm: 3 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.04)",
                }}
              >
                <UploadFile
                  sx={{
                    color: "#12903C",
                    fontSize: { xs: "1.5rem", sm: "1.75rem" },
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: { xs: 0.25, sm: 0.5 },
                    fontSize: { xs: "1rem", sm: "1.125rem" },
                    color: "text.primary",
                  }}
                >
                  Upload CSV File
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  }}
                >
                  Select a CSV file from your computer to start the bulk upload process.
                </Typography>
              </Box>
            </Box>
            <StyledUploadButton
              variant="contained"
              size="large"
              onClick={() => openFileSelector()}
              startIcon={<UploadFile />}
              loading={loading}
              sx={{
                width: { xs: "100%", sm: "auto" },

                mb: { xs: 5, sm: 0 },
              }}
            >
              {loading ? "Processing..." : "Choose CSV File"}
            </StyledUploadButton>
          </Box>

          {/* Results Section */}
          {resultStrings.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1.5, sm: 2 },
                    mb: { xs: 2, sm: 2.5 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 },
                      borderRadius: 2,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.04)",
                    }}
                  >
                    <Description
                      sx={{
                        color: "#12903C",
                        fontSize: { xs: "1.5rem", sm: "1.75rem" },
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: { xs: 0.25, sm: 0.5 },
                        fontSize: { xs: "1rem", sm: "1.125rem" },
                        color: "text.primary",
                      }}
                    >
                      Upload Results
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      Review the results of your bulk upload operation below.
                    </Typography>
                  </Box>
                </Box>
                <StyledResultBox elevation={0}>
                  <Typography
                    component="pre"
                    sx={{
                      fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
                      fontSize: "0.875rem",
                      lineHeight: 1.8,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      m: 0,
                    }}
                  >
                    {resultStrings}
                  </Typography>
                </StyledResultBox>
              </Box>
            </>
          )}
        </Stack>
      </CardBase>
    </Box>
  );
};
export default ProjectBulkUploadPage;
